import {
  HEADER_FIELD_ORDER,
  PRODUCT_FIELD_ORDER,
  AMOUNT_FIELD_ORDER,
  FOOTER_FIELD_ORDER,
  PRINT_FIELD_GROUP,
  getSaleFieldMeta,
} from './stallDotMatrixPrintFieldMeta';
import { toApiPrintStyle, HEADER_IMAGE_ALIGN_TO_API } from './stallDotMatrixPrintShared';

const FIELD_GROUP_SPLIT_CONFIG = [
  { key: 'header', order: HEADER_FIELD_ORDER, group: PRINT_FIELD_GROUP.HEADER },
  { key: 'product', order: PRODUCT_FIELD_ORDER, group: PRINT_FIELD_GROUP.PRODUCT },
  { key: 'amount', order: AMOUNT_FIELD_ORDER, group: PRINT_FIELD_GROUP.AMOUNT },
  { key: 'footer', order: FOOTER_FIELD_ORDER, group: PRINT_FIELD_GROUP.FOOTER },
];

/**
 * 详情/列表记录统一为编辑页可用的 record（含 templateDetail）
 */
export function normalizeNeedleOrderDetailToRecord(data) {
  if (!data) return {};
  if (data.templateDetail) {
    return {
      id: data.id,
      templateName: data.templateName,
      templateDetail: data.templateDetail,
      cxmJolimarkTypeEnum: data.cxmJolimarkTypeEnum || data.templateDetail.printStyle,
      sourceType: data.sourceType,
      defaultTemplateFlag: data.defaultTemplateFlag,
      createTime: data.createTime,
    };
  }
  if (data.templateBO) {
    return {
      id: data.id,
      templateName: data.templateName,
      templateDetail: data.templateBO,
      cxmJolimarkTypeEnum: data.cxmJolimarkTypeEnum || data.templateBO.printStyle,
      sourceType: data.sourceType,
      defaultTemplateFlag: data.defaultTemplateFlag,
      createTime: data.createTime,
    };
  }
  return data;
}

function pickOrderedFields(orderArr, selected) {
  const set = new Set(selected);
  return orderArr.filter((code) => set.has(code));
}

/** InputNumber 未填时按 0 提交 */
function resolveInputNumber(value, fallback = 0) {
  return value != null && value !== '' ? Number(value) : fallback;
}

function buildImageEntity(imageUrl, align, height) {
  if (!imageUrl) return undefined;
  const entity = { imageUrl };
  if (align) {
    entity.align = HEADER_IMAGE_ALIGN_TO_API[align] || 'RIGHT';
  }
  if (height != null) {
    entity.height = height;
  }
  return entity;
}

/** 表单各分组勾选 → 打印字段列表（头部/货品/金额/尾部顺序） */
export function mergeSelectedFieldsToFieldList(
  selectedHeader,
  selectedProduct,
  selectedAmount,
  selectedFooter,
) {
  return [
    ...pickOrderedFields(HEADER_FIELD_ORDER, selectedHeader),
    ...pickOrderedFields(PRODUCT_FIELD_ORDER, selectedProduct),
    ...pickOrderedFields(AMOUNT_FIELD_ORDER, selectedAmount),
    ...pickOrderedFields(FOOTER_FIELD_ORDER, selectedFooter),
  ];
}

/** 接口 fieldList / productFieldList → 各分组已选字段 */
export function splitFieldListToSelectedGroups(detail) {
  const empty = { header: [], product: [], amount: [], footer: [] };
  if (!detail) return empty;

  const fieldListSource =
    (Array.isArray(detail.fieldList) && detail.fieldList.length > 0 && detail.fieldList) ||
    (Array.isArray(detail.productFieldList) &&
      detail.productFieldList.length > 0 &&
      detail.productFieldList) ||
    null;

  if (!fieldListSource) return empty;

  const result = { header: [], product: [], amount: [], footer: [] };

  fieldListSource.forEach((code) => {
    for (const { key, order, group } of FIELD_GROUP_SPLIT_CONFIG) {
      if (!order.includes(code) || result[key].includes(code)) continue;
      if (getSaleFieldMeta(code, group)) {
        result[key].push(code);
        break;
      }
    }
  });

  return result;
}

/**
 * 将编辑页表单状态组装为新增/修改接口 templateBO
 * 仅包含 printStyle、productFieldList（头部/货品/金额/尾部勾选字段合并提交）
 */
export function buildNeedleOrderTemplateBO(formState) {
  const {
    printStyle,
    titleVisible,
    titleRows,
    headerImageUrl,
    headerImageAlign,
    headerImageHeight,
    selectedHeader,
    selectedProduct,
    selectedAmount,
    selectedFooter,
    customContent,
    qrImageUrl,
    qrImageAlign,
    qrImageHeight,
  } = formState;

  const templateBO = {
    printStyle: toApiPrintStyle(printStyle),
    titleBO: {
      showTitle: titleVisible,
      ...(titleVisible ? {} : { height: resolveInputNumber(titleRows) }),
    },
    fieldList: mergeSelectedFieldsToFieldList(
      selectedHeader,
      selectedProduct,
      selectedAmount,
      selectedFooter,
    ),
  };
  const trimmedCustom = (customContent || '').trim();
  if (trimmedCustom) {
    templateBO.customContent = trimmedCustom;
  }

  const headerImage = buildImageEntity(
    headerImageUrl,
    headerImageAlign,
    resolveInputNumber(headerImageHeight),
  );
  if (headerImage) {
    templateBO.headerImage = headerImage;
  }

  const qrCodeImage = buildImageEntity(qrImageUrl, qrImageAlign, resolveInputNumber(qrImageHeight));
  if (qrCodeImage) {
    templateBO.qrCodeImage = qrCodeImage;
  }
  return templateBO;
}

/**
 * 组装销售单针式模板新增/编辑请求体
 * @param {object} formState 表单状态
 * @param {{ stallId: number, id?: number }} options 编辑时传入 id
 */
export function buildNeedleOrderTemplateSaveParams(formState, { stallId, id }) {
  const params = {
    stallId,
    templateName: (formState.templateName || '').trim(),
    templateBO: buildNeedleOrderTemplateBO(formState),
  };
  if (id != null) {
    params.id = id;
  }
  return params;
}
